import { Trans } from '@lingui/react/macro';
import React from 'react';
import { Pages } from 'modules/navigation/domain/enums/pages';
import {
  devicesRecorderButton,
  devicesRecorderModalObs,
  devicesRecorderSaved,
  obsAddBrowser,
  obsAddSource,
  obsBoardPreview,
  obsBrowserProps,
  obsHotkeys,
  obsHotkeysF9,
  obsInteractClick,
  obsInteractPanel,
  obsMarkersRecording,
  obsRecordFormat,
  obsServiceMenu,
  obsSettingsButton,
  obsSettingsOutput,
  obsStartRecording,
  obsWsInfo,
  obsWsSettings,
} from 'modules/video-markers/ui/assets/guide';
import UDLink from 'ud-ui/components/link';
import UDText from 'ud-ui/components/text';
import GuideStep from './guide-step';

/** Настройка OBS Studio по шагам. Подписи кнопок — ровно как в русском интерфейсе OBS 32. */
function ObsGuide() {
  return (
    <div className="flex flex-col gap-4">
      <GuideStep
        number={1}
        title={<Trans>Включите сервер WebSocket</Trans>}
        clickShots={[obsServiceMenu, obsWsSettings]}
        resultShot={obsWsInfo}
      >
        <UDText variant="body-2">
          <Trans>
            В главном окне OBS откройте меню <b>«Сервис»</b> → <b>«Настройки сервера WebSocket»</b>. Поставьте галочку{' '}
            <b>«Включить сервер WebSocket»</b>. Порт оставьте <b>4455</b>, галочку <b>«Включить вход в аккаунт»</b> не снимайте.
          </Trans>
        </UDText>
        <UDText variant="body-2">
          <Trans>
            Нажмите <b>«Показать сведения о подключении»</b> — откроется окно «Сведения о подключении WebSocket». Скопируйте пароль
            кнопкой справа от строки <b>«Пароль сервера»</b> — он нужен на следующем шаге. Закройте это окно, затем «Применить» и «ОК».
            Перезапускать OBS не нужно.
          </Trans>
        </UDText>
      </GuideStep>

      <GuideStep
        number={2}
        title={<Trans>Скопируйте ссылку на табло</Trans>}
        clickShots={[devicesRecorderButton, devicesRecorderModalObs]}
        resultShot={devicesRecorderSaved}
      >
        <UDText variant="body-2">
          <Trans>
            В админке откройте <UDLink to={Pages.DEVICES.INDEX}>«Устройства»</UDLink> и нажмите кнопку <b>«Видеометки: выкл»</b>. В
            поле «Программа» выберите <b>OBS</b>, в поле <b>«Пароль сервера WebSocket»</b> вставьте пароль из шага 1 и нажмите
            «Сохранить». Кнопка станет называться «Видеометки: OBS».
          </Trans>
        </UDText>
        <UDText variant="body-2">
          <Trans>
            В строке нужного устройства нажмите иконку-цепочку <b>«Копировать ссылку на табло»</b>. Если потом поменяете настройку
            видеометок, ссылку нужно скопировать и вставить в OBS заново.
          </Trans>
        </UDText>
      </GuideStep>

      <GuideStep
        number={3}
        title={<Trans>Добавьте табло в OBS</Trans>}
        clickShots={[obsAddSource, obsAddBrowser, obsBrowserProps]}
        resultShot={obsBoardPreview}
      >
        <UDText variant="body-2">
          <Trans>
            В блоке <b>«Источники»</b> нажмите <b>«+»</b> — откроется окно «Добавить источник». Слева выберите <b>«Браузер»</b>, справа
            нажмите <b>«Создать новый источник»</b>. OBS сразу откроет окно свойств источника.
          </Trans>
        </UDText>
        <UDText variant="body-2">
          <Trans>
            В поле <b>«Ссылка»</b> вставьте ссылку из шага 2, <b>«Ширина»</b> — 1920, <b>«Высота»</b> — 1080. Галочка{' '}
            <b>«Отключать источник, когда не видим»</b> должна быть снята (так по умолчанию) — иначе метки не ставятся, пока сцена с
            табло не в эфире. Нажмите «ОК».
          </Trans>
        </UDText>
        <UDText variant="body-2">
          <Trans>
            В окне предпросмотра появится рамка табло. Пока в панели управления не выбран шаблон табло, картинка пустая — это
            нормально. Панель управления справа на трансляцию не попадает.
          </Trans>
        </UDText>
      </GuideStep>

      <GuideStep number={4} title={<Trans>Проверьте подключение</Trans>} clickShots={[obsInteractClick]} resultShot={obsInteractPanel}>
        <UDText variant="body-2">
          <Trans>
            Выберите источник табло в блоке «Источники» и нажмите <b>«Взаимодействовать»</b> над списком (или правой кнопкой мыши по
            источнику → «Взаимодействовать»). В открывшемся окне справа — панель управления, в ней блок <b>«Видеометки · OBS»</b>.
            Должно быть написано <b>«OBS подключён»</b>; «Запись не идёт» до начала записи — это нормально.
          </Trans>
        </UDText>
        <UDText variant="body-2">
          <Trans>
            «Нет подключения: неверный пароль» — пароль в ссылке не совпал с паролем OBS: повторите шаги 1–2 и вставьте новую ссылку в
            источник. «Нет подключения к OBS» — сервер WebSocket выключен (шаг 1).
          </Trans>
        </UDText>
      </GuideStep>

      <GuideStep
        number={5}
        title={<Trans>Проверьте формат записи</Trans>}
        clickShots={[obsSettingsButton, obsSettingsOutput]}
        resultShot={obsRecordFormat}
      >
        <UDText variant="body-2">
          <Trans>
            В блоке «Управление» главного окна нажмите <b>«Настройки»</b> → слева <b>«Вывод»</b> → блок «Запись» →{' '}
            <b>«Формат записи»</b>. Нужен <b>«Гибридный MP4 (.mp4)»</b> (в новом профиле OBS он уже выбран) — тогда метки появятся ещё
            и главами в самом файле записи. В другом формате метки всё равно сохраняются в ImGame, только без глав в файле.
          </Trans>
        </UDText>
      </GuideStep>

      <GuideStep number={6} title={<Trans>Назначьте клавишу метки</Trans>} clickShots={[obsHotkeys]} resultShot={obsHotkeysF9}>
        <UDText variant="body-2">
          <Trans>
            После шага 4 табло само создало в OBS сцену <b>«ImGame метки»</b>. Её не удаляйте и в эфир не выводите — она служебная.
          </Trans>
        </UDText>
        <UDText variant="body-2">
          <Trans>
            <b>«Настройки»</b> → слева <b>«Горячие клавиши»</b> → в поле <b>«Фильтр»</b> наберите «ImGame» → блок «ImGame метки» →
            строка <b>«Показать „ImGame метка“»</b> → нажмите клавишу, например <b>F9</b>. «Скрыть» не назначайте. Клавиша работает,
            даже когда OBS свёрнут и в эфире другая сцена.
          </Trans>
        </UDText>
        <UDText variant="body-2">
          <Trans>Зачем эта клавиша и что будет при разных нажатиях — в блоке «Клавиша метки» в начале страницы.</Trans>
        </UDText>
      </GuideStep>

      <GuideStep number={7} title={<Trans>Проверьте метки</Trans>} clickShots={[obsStartRecording]} resultShot={obsMarkersRecording}>
        <UDText variant="body-2">
          <Trans>
            В блоке «Управление» нажмите <b>«Начать запись»</b>. В панели (шаг 4) появится «● Запись» с таймером. Отметьте событие
            матча в приложении статиста — счётчик «События» в панели увеличится. Нажмите клавишу метки или кнопку <b>«⭐ Метка»</b> —
            увеличится счётчик «ручные».
          </Trans>
        </UDText>
        <UDText variant="body-2">
          <Trans>
            Пока идут часы матча, раз в полминуты растёт счётчик «опорные точки». Строка «Последнее событие» показывает его место в
            записи двумя способами — по хронометражу и по получению; обычно они расходятся на секунду-две.
          </Trans>
        </UDText>
      </GuideStep>
    </div>
  );
}

export default React.memo(ObsGuide);
